import { LtiClaims } from '../types';

export type UserRole = 'learner' | 'instructor' | 'administrator';

/**
 * Canvas LTI role URIs
 */
const CANVAS_ROLES = {
  LEARNER: [
    'http://purl.imsglobal.org/vocab/lis/v2/membership#Learner',
    'Learner',
    'Student',
  ],
  INSTRUCTOR: [
    'http://purl.imsglobal.org/vocab/lis/v2/membership#Instructor',
    'http://purl.imsglobal.org/vocab/lis/v2/institution/person#Instructor',
    'Instructor',
    'Teacher',
    'TeachingAssistant',
  ],
  ADMINISTRATOR: [
    'http://purl.imsglobal.org/vocab/lis/v2/institution/person#Administrator',
    'Administrator',
    'urn:lti:role:ims/lis/Administrator',
  ],
};

/**
 * Determine user's primary role from Canvas LTI roles
 */
export const getUserRole = (user: LtiClaims | null): UserRole => {
  if (!user?.roles || user.roles.length === 0) {
    return 'learner'; // Default to most restrictive
  }

  const userRoles = user.roles.map(r => r.toLowerCase());

  // Check administrator first (highest privilege)
  if (CANVAS_ROLES.ADMINISTRATOR.some(role => 
    userRoles.some(ur => ur.includes(role.toLowerCase()))
  )) {
    return 'administrator';
  }

  // Then check instructor
  if (CANVAS_ROLES.INSTRUCTOR.some(role => 
    userRoles.some(ur => ur.includes(role.toLowerCase()))
  )) {
    return 'instructor';
  }

  // Default to learner
  return 'learner';
};

/**
 * Check if user has specific role
 */
export const hasRole = (user: LtiClaims | null, role: UserRole): boolean => {
  return getUserRole(user) === role;
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user: LtiClaims | null, roles: UserRole[]): boolean => {
  const userRole = getUserRole(user);
  return roles.includes(userRole);
};

/**
 * Check if user can access admin features
 */
export const canAccessAdmin = (user: LtiClaims | null): boolean => {
  return hasRole(user, 'administrator');
};

/**
 * Check if user can verify OTJ hours
 */
export const canVerifyOTJ = (user: LtiClaims | null): boolean => {
  return hasAnyRole(user, ['instructor', 'administrator']);
};

/**
 * Check if user can manage apprentices
 */
export const canManageApprentices = (user: LtiClaims | null): boolean => {
  return hasAnyRole(user, ['instructor', 'administrator']);
};

/**
 * Get default landing page for user role
 */
export const getDefaultRoute = (user: LtiClaims | null): string => {
  const role = getUserRole(user);
  
  switch (role) {
    case 'administrator':
      return '/admin';
    case 'instructor':
      return '/tracking';
    case 'learner':
      return '/dashboard';
    default:
      return '/';
  }
};

/**
 * Get allowed routes for user role
 */
export const getAllowedRoutes = (user: LtiClaims | null): string[] => {
  const role = getUserRole(user);
  
  const routes = {
    learner: [
      '/',
      '/dashboard',
      '/tracking/otj/new',
      '/standards',
    ],
    instructor: [
      '/',
      '/dashboard',
      '/apprentices',
      '/apprentices/:id',
      '/tracking',
      '/tracking/otj/new',
      '/standards',
    ],
    administrator: [
      '/',
      '/admin',
      '/dashboard',
      '/apprentices',
      '/apprentices/new',
      '/apprentices/:id',
      '/tracking',
      '/tracking/otj/new',
      '/standards',
      '/ilr',
    ],
  };
  
  return routes[role] || routes.learner;
};
